/**
 * @since 2026-04-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useState } from "react";
import { History as HistoryIcon, Logout as LogoutIcon } from "@mui/icons-material";
import { Badge, IconButton, Toolbar, Tooltip, Typography } from "@mui/material";
import FixJobHistoryDialog from "components/Shared/FixJobHistoryDialog";
import { getConfig } from "../../getConfig";
import "./MenuBar.scss";

const MenuBar = ({ user, onLogout, onNavigateHome, idToken, getToken, activeJobs = [], pendingOverrides = [], pendingOverrideCount = 0, refetchPendingOverrides, canRunFixJob = false }) => {
  const displayName = getConfig().DISPLAY_NAME || "DATASET REVIEW";
  const logoUrl = getConfig().auth?.image_url || "https://www.algotraders.ai/images/Logo.png";
  const [historyOpen, setHistoryOpen] = useState(false);
  const activeCount = activeJobs.length;
  const needsAttention = activeCount === 0 && pendingOverrideCount > 0;
  const badgeCount = activeCount > 0 ? activeCount : pendingOverrideCount;
  const jobsTooltip = activeCount > 0
    ? `Fix jobs (${activeCount} active)`
    : pendingOverrideCount > 0
      ? `${pendingOverrideCount} pending override${pendingOverrideCount === 1 ? "" : "s"} — click to run fix job`
      : "Fix jobs";

  return (
    <div className="menuBar">
      <Toolbar
        className="customToolbar"
        sx={{
          minHeight: "32px !important",
          paddingTop: "8px !important",
          paddingBottom: "8px !important",
        }}
      >
        <div
          className="titleSection"
          role={onNavigateHome ? "button" : undefined}
          tabIndex={onNavigateHome ? 0 : undefined}
          onClick={onNavigateHome}
          onKeyDown={(e) => {
            if (onNavigateHome && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onNavigateHome();
            }
          }}
        >
          <img src={logoUrl} alt="AlgoTraders Logo" />
          <Typography variant="h6" component="div">{displayName}</Typography>
        </div>

        <div className="rightSection">
          {user && (
            <Tooltip title={jobsTooltip}>
              <Badge
                color={needsAttention ? "warning" : "primary"}
                badgeContent={badgeCount}
                invisible={badgeCount === 0}
                overlap="circular"
                className={`menuBar__jobsBadge${needsAttention ? " menuBar__jobsBadge--attention" : ""}`}
              >
                <IconButton
                  onClick={() => setHistoryOpen(true)}
                  size="small"
                  className={`menuButton${needsAttention ? " menuButton--attention" : ""}`}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>
          )}
          {user && (
            <Tooltip title="Log out">
              <IconButton onClick={onLogout} size="small" className="menuButton">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Toolbar>
      <FixJobHistoryDialog
        open={historyOpen}
        idToken={idToken}
        getToken={getToken}
        canRun={canRunFixJob}
        pendingOverrides={pendingOverrides}
        refetchPendingOverrides={refetchPendingOverrides}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
};

export default MenuBar;
